import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import type { Article } from '@/mocks/articles';
import { formatViews } from '@/utils/article';

interface Props {
  slides: Article[];
}

export default function HeroCarousel({ slides }: Props) {
  const [current, setCurrent] = useState(0);
  const touchStart = useRef<number | null>(null);
  const paused = useRef(false);

  useEffect(() => {
    if (slides.length <= 1) return;
    const timer = setInterval(() => {
      if (!paused.current) setCurrent((c) => (c + 1) % slides.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [slides.length]);

  if (slides.length === 0) {
    return (
      <div className="w-full h-[360px] md:h-[520px] rounded-xl bg-background-200 flex items-center justify-center">
        <p className="text-foreground-500 text-sm">暂无焦点内容，敬请期待更多大瓜</p>
      </div>
    );
  }

  const go = (dir: number) => setCurrent((c) => (c + dir + slides.length) % slides.length);

  return (
    <div
      className="relative w-full h-[420px] md:h-[560px] rounded-xl overflow-hidden bg-primary-950"
      onMouseEnter={() => { paused.current = true; }}
      onMouseLeave={() => { paused.current = false; }}
      onTouchStart={(e) => { touchStart.current = e.touches[0].clientX; }}
      onTouchEnd={(e) => {
        if (touchStart.current === null) return;
        const diff = e.changedTouches[0].clientX - touchStart.current;
        if (Math.abs(diff) > 50) go(diff > 0 ? -1 : 1);
        touchStart.current = null;
      }}
    >
      {slides.map((slide, i) => (
        <div
          key={slide.id}
          className={`absolute inset-0 transition-opacity duration-700 ${i === current ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
        >
          <img
            src={slide.cover}
            alt={slide.title}
            title={`${slide.title} - 黑料网曝大瓜`}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-primary-950/90 via-primary-950/40 to-primary-950/20" />
        </div>
      ))}

      <div className="relative z-10 h-full flex flex-col justify-end p-6 md:p-12 w-full">
        <div className="flex items-center gap-2 mb-3">
          <span className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full bg-secondary-500 text-secondary-950 font-label font-semibold">
            <i className="ri-fire-fill" /> 今日大瓜
          </span>
          <span className="text-xs px-2.5 py-1 rounded-full bg-background-50/20 text-background-50 backdrop-blur-sm">
            {slides[current].categoryName} · {slides[current].sub}
          </span>
        </div>
        <Link to={`/detail/${slides[current].id}`} className="group max-w-2xl cursor-pointer">
          <h1 className="font-heading font-extrabold text-2xl md:text-4xl text-background-50 leading-tight group-hover:text-secondary-300 transition-colors">
            {slides[current].title}
          </h1>
          <p className="mt-3 text-sm md:text-base text-background-50/80 line-clamp-2 max-w-xl">
            {slides[current].summary}
          </p>
        </Link>
        <div className="mt-5 flex items-center gap-4">
          <Link
            to={`/detail/${slides[current].id}`}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            查看完整爆料 <i className="ri-arrow-right-line" />
          </Link>
          <span className="flex items-center gap-1.5 text-xs text-background-50/70">
            <i className="ri-eye-line" /> {formatViews(slides[current].views)} 阅读
          </span>
        </div>
      </div>

      <button
        type="button"
        onClick={() => go(-1)}
        aria-label="上一张"
        className="hidden md:flex absolute left-4 top-1/2 -translate-y-1/2 z-20 w-10 h-10 items-center justify-center rounded-full bg-background-50/20 backdrop-blur text-background-50 hover:bg-background-50/40 transition-colors cursor-pointer"
      >
        <i className="ri-arrow-left-s-line text-xl" />
      </button>
      <button
        type="button"
        onClick={() => go(1)}
        aria-label="下一张"
        className="hidden md:flex absolute right-4 top-1/2 -translate-y-1/2 z-20 w-10 h-10 items-center justify-center rounded-full bg-background-50/20 backdrop-blur text-background-50 hover:bg-background-50/40 transition-colors cursor-pointer"
      >
        <i className="ri-arrow-right-s-line text-xl" />
      </button>

      <div className="absolute bottom-5 right-6 md:right-12 z-20 flex items-center gap-2">
        {slides.map((s, i) => (
          <button
            key={s.id}
            type="button"
            aria-label={`第${i + 1}张`}
            onClick={() => setCurrent(i)}
            className={`h-1.5 rounded-full transition-all cursor-pointer ${i === current ? 'w-6 bg-secondary-500' : 'w-3 bg-background-50/50 hover:bg-background-50/80'}`}
          />
        ))}
      </div>
    </div>
  );
}